import { PropertySection } from "../component-utils/property-sections";
import { getEnumProperty } from "../component-utils/property-utils";
import { WireComponentKind } from "../wire/component-kind";
import type { WireComponent } from "../wire/types";
import { HeroImageEffect, UIBackgroundStyle } from "../wire/types";
import type { WireButtonComponent } from "./fluent-components";
import type { WirePropertiesOfComponent } from "./fluent-components-spec";
import { ContainerKind, emptyProperties, FluentComponent, properties } from "./fluent-components-spec";

function container<TKind extends WireComponentKind>(kind: TKind) {
    return new FluentComponent<
        unknown,
        unknown,
        { readonly kind: TKind; readonly components: readonly (WireComponent | null)[] }
    >({
        kind,
        containerKind: ContainerKind.Regular,
        properties: emptyProperties,
        formProperties: emptyProperties,
        includeRegularComponent: true,
    });
}

function formContainer<TKind extends WireComponentKind>(kind: TKind) {
    return new FluentComponent<
        unknown,
        unknown,
        {
            readonly kind: TKind;
            readonly components: readonly (WireComponent | null)[];
            readonly specialButtons: readonly (WireButtonComponent | null)[];
        }
    >({
        kind,
        containerKind: ContainerKind.Form,
        properties: emptyProperties,
        formProperties: emptyProperties,
        includeRegularComponent: true,
    });
}

// NOTE: These are all our container components.  When adding a new one, make
// sure to add it do ##isWireContainerComponent.
export const Container = container(WireComponentKind.Container)
    .withDisplayName("Container")
    .withIcon("mt-component-container-new")
    .withDescription("Group multiple components together and edit the background")
    .withProperties(
        properties
            .withEnum(
                "containerStyle",
                [
                    UIBackgroundStyle.None,
                    UIBackgroundStyle.Card,
                    UIBackgroundStyle.Accent,
                    UIBackgroundStyle.Dark,
                    UIBackgroundStyle.Highlight,
                    UIBackgroundStyle.Image,
                ],
                {
                    propertySection: PropertySection.Design,
                }
            )
            .withImage("backgroundImage", {
                propertySection: PropertySection.Design,
                when: d => getEnumProperty(d.containerStyle) === UIBackgroundStyle.Image,
                subcomponent: {
                    name: "Background",
                    important: true,
                },
            })
            .withEnum("backgroundEffect", [HeroImageEffect.None, HeroImageEffect.Darken, HeroImageEffect.Lighten], {
                displayName: "Background Effect",
                propertySection: PropertySection.Design,
                when: d => getEnumProperty(d.containerStyle) === UIBackgroundStyle.Image,
                subcomponent: {
                    name: "Background",
                    important: true,
                },
            })
            .withSwitch("withBlur", false, {
                propertySection: PropertySection.Design,
                when: d => getEnumProperty(d.containerStyle) === UIBackgroundStyle.Image,
                subcomponent: {
                    name: "Background",
                    important: true,
                },
            })
    );
export type WireContainerComponent = WirePropertiesOfComponent<typeof Container>;

export const FormContainer = formContainer(WireComponentKind.FormContainer)
    .withDisplayName("Form Container")
    .withFeatureSetting("formContainer")
    .withDescription("Attach a group of form components to any source in your project.")
    .withIcon("mt-component-container-form")
    .withProperties(
        properties.withEnum(
            "containerStyle",
            [
                UIBackgroundStyle.None,
                UIBackgroundStyle.Card,
                UIBackgroundStyle.Accent,
                UIBackgroundStyle.Dark,
                UIBackgroundStyle.Highlight,
            ],
            { propertySection: PropertySection.Design }
        )
    );
export type WireFormContainerComponent = WirePropertiesOfComponent<typeof FormContainer>;
